import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTheme } from '../../context/ThemeContext';
import {
  PaintBrushIcon,
  BellIcon,
  ShieldCheckIcon,
  TrashIcon,
  CheckIcon,
  SunIcon,
  MoonIcon
} from '@heroicons/react/24/outline';

import { userService } from '../../services/user';

const Settings = () => {
  const { logout } = useAuth();
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { theme, font, updateTheme, updateFont } = useTheme();

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
    setValue,
    watch
  } = useForm();

  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const response = await userService.getProfile();
        const userPrefs = response.data.user.preferences;
        setPreferences(userPrefs);
        
        // Set form values
        setValue('theme', theme);
        setValue('font', font);
        setValue('reminders.enabled', userPrefs.reminders.enabled);
        setValue('reminders.time', userPrefs.reminders.time);
        
        setLoading(false);
      } catch (error) {
        toast.error('Failed to load settings');
        setLoading(false);
      }
    };

    fetchPreferences();
  }, [theme, font,setValue]);

  const onSubmit = async (data) => {
    try {
      // Update theme and font immediately
      updateTheme(data.theme);
      updateFont(data.font);
      
      // Save to backend
      await userService.updatePreferences(data);
      toast.success('Settings updated successfully');
      setPreferences(data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update settings');
    }
  };

  const handleDeleteAccount = async () => {
    const password = prompt('Please enter your password to confirm account deletion:');
    if (!password) return;

    try {
      await userService.deleteAccount(password);
      toast.success('Account deleted successfully');
      logout();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete account');
    }
  };

  const currentTheme = watch('theme');

  if (loading) {
    return (
      <div className="min-h-screen bg-paper-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-500 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl font-serif font-bold mb-2">Settings</h1>
            <p className="text-purple-100 text-lg">
              Customize your DiaryVerse experience
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          {/* Appearance Settings */}
          <motion.div
            className="card p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <PaintBrushIcon className="w-6 h-6 text-purple-600" />
              <h2 className="text-xl font-semibold text-ink-black">Appearance</h2>
            </div>

            <div className="space-y-6">
              {/* Theme Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Theme
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <motion.label
                    className={`relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      currentTheme === 'light'
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <input
                      {...register('theme')}
                      type="radio"
                      value="light"
                      className="sr-only"
                    />
                    <SunIcon className="w-6 h-6 text-yellow-500 mr-3" />
                    <div>
                      <div className="font-medium text-gray-900">Light</div>
                      <div className="text-sm text-gray-500">Clean and bright</div>
                    </div>
                    {currentTheme === 'light' && (
                      <CheckIcon className="w-5 h-5 text-purple-600 ml-auto" />
                    )}
                  </motion.label>

                  <motion.label
                    className={`relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      currentTheme === 'dark'
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <input
                      {...register('theme')}
                      type="radio"
                      value="dark"
                      className="sr-only"
                    />
                    <MoonIcon className="w-6 h-6 text-purple-500 mr-3" />
                    <div>
                      <div className="font-medium text-gray-900">Dark</div>
                      <div className="text-sm text-gray-500">Easy on the eyes</div>
                    </div>
                    {currentTheme === 'dark' && (
                      <CheckIcon className="w-5 h-5 text-purple-600 ml-auto" />
                    )}
                  </motion.label>
                </div>
              </div>

              {/* Font Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Font Family
                </label>
                <select
                  {...register('font')}
                  className="input-field"
                >
                  <option value="Inter">Inter (Default)</option>
                  <option value="Crimson Text">Crimson Text (Serif)</option>
                  <option value="JetBrains Mono">JetBrains Mono (Monospace)</option>
                  <option value="Georgia">Georgia (Serif)</option>
                  <option value="Helvetica">Helvetica (Sans-serif)</option>
                </select>
              </div>
            </div>
          </motion.div>

          {/* Notification Settings */}
          <motion.div
            className="card p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <BellIcon className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-semibold text-ink-black">Notifications</h2>
            </div>

            <div className="space-y-6">
              {/* Daily Reminders */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-gray-900">Daily Writing Reminders</h3>
                  <p className="text-sm text-gray-500">
                    Get reminded to write in your diary every day
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    {...register('reminders.enabled')}
                    type="checkbox"
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Reminder Time */}
              {watch('reminders.enabled') && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reminder Time
                  </label>
                  <input
                    {...register('reminders.time')}
                    type="time"
                    className="input-field max-w-xs"
                  />
                </motion.div>
              )}
            </div>
          </motion.div>

          {/* Privacy & Security */}
          <motion.div
            className="card p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <ShieldCheckIcon className="w-6 h-6 text-green-600" />
              <h2 className="text-xl font-semibold text-ink-black">Privacy & Security</h2>
            </div>

            <div className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <ShieldCheckIcon className="w-5 h-5 text-green-600" />
                  <h3 className="font-medium text-green-800">Your Data is Secure</h3>
                </div>
                <p className="text-sm text-green-700">
                  All your diary entries are encrypted and stored securely. We never share your personal information with third parties.
                </p>
              </div>

              {/* Account Deletion */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="font-medium text-gray-900 mb-2">Danger Zone</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Once you delete your account, there is no going back. Please be certain.
                </p>
                <motion.button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <TrashIcon className="w-4 h-4" />
                  <span>Delete Account</span>
                </motion.button>
              </div>
            </div>
          </motion.div>

          {/* Save Button */}
          <motion.div
            className="flex justify-end"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <motion.button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary flex items-center space-x-2"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <CheckIcon className="w-4 h-4" />
                  <span>Save Settings</span>
                </>
              )}
            </motion.button>
          </motion.div>
        </form>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <motion.div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="bg-white rounded-xl p-6 max-w-md w-full"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
          >
            <div className="flex items-center space-x-3 mb-4">
              <TrashIcon className="w-6 h-6 text-red-600" />
              <h3 className="text-lg font-semibold text-gray-900">Delete Account</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you absolutely sure you want to delete your account? This action cannot be undone and will permanently delete all your diary entries.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete Account
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default Settings;
